import {isFunction, isObject} from 'es-toolkit/compat'

import {loadScript, waitForApi} from './critical'

export type RequiredDixaFormData = {
  name: string
  email: string
  subject: string
  message: string
}

export type DixaFormData = RequiredDixaFormData & {
  [key: string]: string | boolean | undefined
}

const RETRY_LIMIT = 5
const RETRY_INTERVAL = 1000

let dixaInitialized = false
let dixaWidgetElement: Element
let dixaResizeObserver: ResizeObserver

const isDixaBlocked = () => !window._dixa

const dixa = (
  cmd: string,
  options: string | Record<string, unknown>,
  moreOptionsOrCallback?: string | ((...args: never[]) => void)
): void => {
  if (window._dixa) {
    window._dixa(
      cmd,
      isObject(options) && !isFunction(options) ? JSON.stringify(options) : options,
      moreOptionsOrCallback
    )
  }
}

const initResizeObserver = () => {
  if (window.ResizeObserver) {
    dixaResizeObserver = new ResizeObserver(() => {
      if (dixaWidgetElement.clientHeight < 150) {
        window.document.body.classList.remove('u-dixa-visible')
        dixaResizeObserver.disconnect()
      }
    })
  }
}

export const canOpenDixa = () => dixaInitialized && !!dixaWidgetElement && !isDixaBlocked()

export const openDixa = () => {
  dixa('api.setView', 'conversation')

  setTimeout(() => {
    // dixa's "api.setView" takes some time. If we set dixa visible to early,
    // the user would see the minimzed widget shortly before the conversation is visible.
    window.document.body.classList.add('u-dixa-visible')
    if (dixaResizeObserver) {
      dixaResizeObserver.observe(dixaWidgetElement)
    }
  }, 300)
}

export const closeDixa = () => {
  dixa('api.setView', 'hidden')
  window.document.body.classList.remove('u-dixa-visible')
}

export const setUser = (name: string, email: string) => {
  dixa('api.setUser', name, email)
}

export const init = (enabled: boolean, setDefaultUser: boolean) => {
  if (enabled) {
    loadScript(window.CRAFTCLOUD_DIXA_SRC)
    waitForApi(
      () => !!window._dixa,
      () => {
        dixaInitialized = true

        let retries = 0

        // It takes some time until the widget element is fully loaded and accessible
        const timer = setInterval(() => {
          const dixaWidgetWrapper = document.getElementById('dixa-widget-wrapper')
          if (dixaWidgetWrapper) {
            dixaWidgetElement = dixaWidgetWrapper
            initResizeObserver()

            if (setDefaultUser) {
              // The solution to prevent the form inputs was provided by dixa
              // https://github.com/all3dp/craftcloud-client/issues/2127
              dixa('api.setUser', 'Your name', 'youremail@example.com')
            }

            dixa('api.subscribe', 'onMessageAdded', () => openDixa())

            clearInterval(timer)
          }
          if (++retries === RETRY_LIMIT) {
            clearInterval(timer)
          }
        }, RETRY_INTERVAL)
      }
    )
  } else {
    // eslint-disable-next-line no-console
    console.info('*** Dixa disabled ***')
  }
}

export const submitDixaForm = (payload: DixaFormData, endpoint: string) => {
  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => formData.append(key, v))
    } else if (value !== undefined) {
      formData.append(key, `${value}`)
    }
  })

  return fetch(endpoint, {
    method: 'POST',
    body: formData,
  }).then(response => response.json())
}
